import * as styles from './permission-notice.css'

type PermissionNoticeProps = {
  /** `undefined` until the role is known — see `useIsAdmin`. */
  isAdmin: boolean | undefined
  /** Whether the role lookup failed outright — see `useAdminRole`. */
  isRoleUnavailable: boolean
  /**
   * The surface-specific sentence shown to a confirmed non-admin, naming what they cannot do
   * ("Only administrators can edit preferences."). The sentence for an unknown role is the same
   * everywhere and belongs to this component.
   */
  children: React.ReactNode
}

/**
 * The explanation that stands in for a withheld admin-only control.
 *
 * The three role states get three different treatments, and getting them wrong is what this
 * component exists to prevent. A confirmed non-admin is told what they cannot do. A role still in
 * flight is told nothing — it resolves in milliseconds, and guessing wrong would accuse an admin of
 * lacking a permission they hold. A failed lookup says so and points at the way out, because that
 * state never resolves on its own.
 */
export const PermissionNotice: React.FC<PermissionNoticeProps> = ({ isAdmin, isRoleUnavailable, children }) => {
  if (isAdmin === false) {
    return <p className={styles.permissionNotice}>{children}</p>
  }

  if (isAdmin === undefined && isRoleUnavailable) {
    return <p className={styles.permissionNotice}>Unable to confirm your permissions. Reload the page to try again.</p>
  }

  return null
}
